import { useCallback, useMemo, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import {
  PermissionAction,
  PermissionResource,
  useGetUser,
  type InitUploadDocumentResponseItemDto,
} from '@sib/api';
import { useCan, useUserSetupStore } from '@sib/auth';
import type {
  FileUploadStore,
  UploadFileCallback,
  AuditUploadContext,
} from '@sib/files';
import {
  compareFileUploadItems,
  resolveDocumentUploadPoolSize,
  runWithConcurrencyPool,
  initDocumentUploadSessions,
  useUploadToBlobStorage,
  useBatchUpload,
  useDocumentFileMd5Prefetch,
  validateDocumentUploadByMd5,
} from '@sib/files';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { tracking } from '@sib/tracking';
import { FileItem, FileStatus, SortDirection } from '@sib/types';
import {
  DOCUMENTS_DROPZONE_CONFIG,
  getUploadSlotFileCount,
  resolveDropzoneFileLimitOptions,
  useOnDropHandler,
} from '@sib/utils';
import { NotificationToastAction } from '../NotificationToast';

interface UseUploadingFilesListProps {
  fileUploadStore: FileUploadStore;
  closeFileManager?: () => void;
  dropzoneConfig?: Partial<DropzoneOptions>;
  notificationAction?: NotificationToastAction;
  disableBlobUpload?: boolean;
  uploadFileCallback?: UploadFileCallback;
  auditUpload?: Pick<AuditUploadContext, 'auditId'>;
  organization?: {
    id?: string;
    name?: string;
  };
  prefetchDocumentMd5?: boolean;
  defaultDocumentInternal?: boolean;
}

export const useUploadingFilesList = ({
  fileUploadStore,
  closeFileManager,
  dropzoneConfig,
  notificationAction,
  disableBlobUpload = false,
  uploadFileCallback,
  auditUpload,
  organization,
  prefetchDocumentMd5,
  defaultDocumentInternal = false,
}: UseUploadingFilesListProps) => {
  const { t } = useTranslation();
  const can = useCan();
  const canSetDocumentInternal = useMemo(
    () =>
      can({
        permissions: {
          actions: PermissionAction.CREATE,
          resources: PermissionResource.InternalDocuments,
        },
      }),
    [can]
  );
  const documentUploadOptions = useMemo(
    () =>
      defaultDocumentInternal && canSetDocumentInternal
        ? { defaultInternal: true, includeInternalField: true }
        : { includeInternalField: false },
    [defaultDocumentInternal, canSetDocumentInternal]
  );
  const {
    files,
    addFiles: storeAddFiles,
    getFiles,
    onFilesUploaded,
    updateFile,
    selectAll,
    removeUploadedFiles,
    getAreFilesUploading,
    removeFile,
    registerUploadController,
    unregisterUploadController,
  } = fileUploadStore;

  const areFilesUploading = getAreFilesUploading();
  const {
    organization: { id: organizationId, name: organizationName },
  } = useUserSetupStore();
  const effectiveOrganizationId = organization?.id ?? organizationId;
  const effectiveOrganizationName = organization?.name ?? organizationName;
  const resolvedAuditUpload = auditUpload
    ? {
        auditId: auditUpload.auditId,
        organizationId: effectiveOrganizationId ?? '',
      }
    : undefined;
  const { data: userData } = useGetUser();
  const [nameSortOrder, setNameSortOrder] = useState<SortDirection>('asc');
  const toggleSortOrder = () => {
    setNameSortOrder(currentOrder => (currentOrder === 'asc' ? 'desc' : 'asc'));
  };
  const sortedFiles = useMemo(
    () =>
      [...files].sort((a, b) => compareFileUploadItems(a, b, nameSortOrder)),
    [files, nameSortOrder]
  );
  const resolvedDropzoneConfig = resolveDropzoneFileLimitOptions(
    dropzoneConfig || DOCUMENTS_DROPZONE_CONFIG
  );
  const { maxFiles } = resolvedDropzoneConfig;
  const uploadSlotFileCount = getUploadSlotFileCount(files);
  const isMaxFilesReached =
    typeof maxFiles === 'number' && uploadSlotFileCount >= maxFiles;
  const canConfirmUpload = uploadSlotFileCount > 0;

  const shouldPrefetchDocumentMd5 = prefetchDocumentMd5 ?? !disableBlobUpload;

  const addFiles = useCallback(
    (newFiles: FileItem[]) => {
      if (defaultDocumentInternal && canSetDocumentInternal)
        return storeAddFiles(
          newFiles.map(file => ({
            ...file,
            isInternal: file.isInternal ?? true,
          }))
        );

      storeAddFiles(newFiles);
    },
    [storeAddFiles, defaultDocumentInternal, canSetDocumentInternal]
  );

  useDocumentFileMd5Prefetch({
    enabled: shouldPrefetchDocumentMd5,
    files,
    getFiles,
    updateFile,
  });

  const { onDrop } = useOnDropHandler({
    addFiles,
    ...(typeof maxFiles === 'number' && {
      maxFiles,
      getCurrentFileCount: () => getUploadSlotFileCount(getFiles()),
    }),
  });
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...resolvedDropzoneConfig,
    onDrop,
    disabled: isMaxFilesReached,
  });

  const { open } = useNotification();

  const fileUploadCallbacks = useMemo(
    () => ({
      onProgress: (fileItem: FileItem, progress: number) =>
        updateFile({
          ...fileItem,
          status: FileStatus.Uploading,
          progress,
        }),
      onSuccess: (fileItem: FileItem) => {
        if (fileItem.status === FileStatus.Uploaded) return;

        tracking.track('Completed file upload', {
          fileName: fileItem.file.name,
          organizationId: effectiveOrganizationId,
        });

        updateFile({ ...fileItem, status: FileStatus.Uploaded });
      },
      onError: (fileItem: FileItem, error?: string, errorDetails?: unknown) => {
        tracking.track('Failed file upload', {
          error,
          errorDetails: JSON.stringify(errorDetails),
          fileName: fileItem.file.name,
          organizationId: effectiveOrganizationId,
          userOrganizations: JSON.stringify(userData?.organizations),
        });
        updateFile({
          ...fileItem,
          status: FileStatus.Error,
          errorMessage: error,
        });
      },
    }),
    [updateFile, effectiveOrganizationId, userData]
  );
  const { uploadDocumentFile } = useUploadToBlobStorage({
    ...fileUploadCallbacks,
    organizationId: effectiveOrganizationId,
    uploadFileCallback,
    auditUpload: resolvedAuditUpload,
    documentUploadOptions,
  });

  const { generateBatchId } = useBatchUpload();
  const uploadFiles = async () => {
    if (disableBlobUpload) {
      const filesToMarkAsUploaded = files.filter(
        file => file.status !== FileStatus.Error
      );
      if (!filesToMarkAsUploaded.length) return;

      closeFileManager?.();
      filesToMarkAsUploaded.forEach(fileItem => {
        updateFile({
          ...fileItem,
          status: FileStatus.Uploaded,
          progress: 100,
        });
      });
      onFilesUploaded?.();

      return;
    }

    const filesToUpload = getFiles().filter(
      file => file.status !== FileStatus.Error
    );
    if (!filesToUpload.length) return;

    const batchUploadId = generateBatchId();
    const resolveFileItem = (fileId: string) =>
      getFiles().find(file => file.id === fileId);
    const duplicateFileIds = new Set<string>();
    const duplicateErrors: string[] = [];

    filesToUpload.forEach(fileItem => {
      const currentFile = resolveFileItem(fileItem.id) ?? fileItem;

      updateFile({
        ...currentFile,
        status: FileStatus.Uploading,
        progress: 0,
      });
    });

    let validatedFiles: Awaited<ReturnType<typeof validateDocumentUploadByMd5>>;

    try {
      validatedFiles = await validateDocumentUploadByMd5({
        organizationId: effectiveOrganizationId ?? '',
        fileItems: filesToUpload,
        resolveFileItem,
        onDuplicate: (item, error) => {
          duplicateFileIds.add(item.id);
          duplicateErrors.push(error);
          fileUploadCallbacks.onError?.(item, error);
        },
        onMd5Error: (item, error) =>
          fileUploadCallbacks.onError?.(
            item,
            t('fileManager.uploadFailedMessage'),
            error
          ),
      });
    } catch (error) {
      filesToUpload.forEach(fileItem =>
        fileUploadCallbacks.onError?.(
          resolveFileItem(fileItem.id) ?? fileItem,
          t('fileManager.uploadFailedMessage'),
          error
        )
      );

      removeUploadedFiles();

      return;
    }
    const duplicateDescriptions = [...new Set(duplicateErrors.filter(Boolean))];

    if (duplicateDescriptions.length > 0) {
      const [duplicateMessage, ...remainingDuplicateDescriptions] =
        duplicateDescriptions;

      open?.({
        type: 'error',
        message: duplicateMessage,
        description:
          remainingDuplicateDescriptions.length > 0
            ? remainingDuplicateDescriptions.join('\n')
            : undefined,
      });
    }

    if (validatedFiles.length === 0) return;

    const totalFiles = validatedFiles.length;

    closeFileManager?.();
    open?.({
      type: 'info',
      message: t('fileManager.startUploadMessage'),
      description: t('fileManager.startUploadDescription'),
    });

    if (uploadFileCallback)
      await runWithConcurrencyPool(
        validatedFiles,
        () => resolveDocumentUploadPoolSize('upload'),
        async ({ fileItem, md5Hash }) => {
          const currentFile = resolveFileItem(fileItem.id) ?? fileItem;
          const controller = new AbortController();
          registerUploadController(currentFile.id, controller);

          try {
            await uploadDocumentFile(currentFile, {
              batchUploadId,
              totalFiles,
              md5Hash,
              abortSignal: controller.signal,
            });
          } finally {
            unregisterUploadController(currentFile.id);
          }
        }
      );
    else {
      let initSessionsByFilename = new Map<
        string,
        InitUploadDocumentResponseItemDto
      >();

      try {
        const initResult = await initDocumentUploadSessions(
          effectiveOrganizationId ?? '',
          {
            batchUploadId,
            totalFiles,
            files: validatedFiles,
            auditUpload: resolvedAuditUpload,
            documentUploadOptions,
          }
        );

        initSessionsByFilename = initResult.sessionsByFilename;

        initResult.failed.forEach(({ filename, error }) => {
          const failedFile = validatedFiles.find(
            ({ fileItem }) => fileItem.file.name === filename
          );

          if (failedFile)
            fileUploadCallbacks.onError?.(failedFile.fileItem, error);
        });
      } catch (error) {
        validatedFiles.forEach(({ fileItem }) => {
          fileUploadCallbacks.onError?.(
            resolveFileItem(fileItem.id) ?? fileItem,
            t('fileManager.uploadFailedMessage'),
            error
          );
        });

        removeUploadedFiles();

        return;
      }

      const filesReadyToUpload = validatedFiles.filter(({ fileItem }) =>
        initSessionsByFilename.has(fileItem.file.name)
      );

      await runWithConcurrencyPool(
        filesReadyToUpload,
        () => resolveDocumentUploadPoolSize('upload'),
        async ({ fileItem, md5Hash }) => {
          const currentFile = resolveFileItem(fileItem.id) ?? fileItem;
          const controller = new AbortController();
          registerUploadController(currentFile.id, controller);

          try {
            await uploadDocumentFile(currentFile, {
              batchUploadId,
              totalFiles,
              md5Hash,
              abortSignal: controller.signal,
              initSession: initSessionsByFilename.get(fileItem.file.name),
            });
          } finally {
            unregisterUploadController(currentFile.id);
          }
        }
      );
    }

    const currentFiles = getFiles();

    const uploadedCount = currentFiles.filter(
      file => file.status === FileStatus.Uploaded
    ).length;
    const failedFiles = currentFiles.filter(
      file => file.status === FileStatus.Error && !duplicateFileIds.has(file.id)
    );
    const failedCount = failedFiles.length;

    if (failedCount > 0) {
      const specificErrors = [
        ...new Set(
          failedFiles
            .map(file => file.errorMessage)
            .filter((message): message is string => Boolean(message))
        ),
      ];

      open?.({
        type: 'error',
        message: t('fileManager.uploadFailedMessage'),
        description:
          specificErrors.length > 0
            ? specificErrors.join('\n')
            : uploadedCount > 0
              ? t('fileManager.uploadFailedPartialDescription', {
                  uploadedCount,
                  failedCount,
                })
              : t('fileManager.uploadFailedAllFailedDescription', {
                  failedCount,
                }),
      });
    } else if (uploadedCount > 0) {
      open?.({
        type: 'success',
        message: t('fileManager.uploadSuccess.message'),
        description: t('fileManager.uploadSuccess.description', {
          uploadedCount,
        }),
        ...(notificationAction && { action: notificationAction }),
      });
      onFilesUploaded?.();
      closeFileManager?.();
    }

    removeUploadedFiles();
  };

  const hasErrors = files.some(file => file.status === FileStatus.Error);

  const anySelected = files.some(file => file.selected);

  const allSelected =
    files.length > 0 && files.every(fileItem => fileItem.selected);

  const toggleSelectAllFiles = () => {
    selectAll(!allSelected);
  };

  const areThereAnyFiles = files.length > 0;

  return {
    files,
    sortedFiles,
    isDragActive,
    hasErrors,
    canConfirmUpload,
    allSelected,
    areThereAnyFiles,
    areFilesUploading,
    anySelected,
    isMaxFilesReached,
    organizationName: effectiveOrganizationName,
    organizationId: effectiveOrganizationId,
    toggleSelectAllFiles,
    uploadFiles,
    toggleSortOrder,
    getRootProps,
    getInputProps,
    updateFile,
    removeFile,
  };
};
